<script setup lang="ts">
import { format, parseISO } from 'date-fns';
import { computed } from 'vue';

import { STREAM_BASE_URI } from '@/js/constants';
import { useGlobalStyle, useTemplate } from '@/store/templateData';

import ICON_POST_ARROW from '../../assets/icons/post-arrow.svg';
import ICON_POST_PLAY from '../../assets/icons/post-play.svg';
import ICON_REPLAY from '../../assets/icons/replay.svg';

interface ModuleLiveHistoryProps {
  module?: {
    components?: {
      title?: {
        text: string;
      };
      subtitle?: {
        text: string;
      };
    };
  };
}

defineOptions({
  name: 'ModuleLiveHistoryDisplay'
});

const POST_NUMBER_PER_ROW = 4;

const props = withDefaults(defineProps<ModuleLiveHistoryProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
    },
  }),
});

const components = computed(() => props.module?.components);
const { liveHistoryList, handsupConfig } = useTemplate();

const slug = computed(() => handsupConfig.value?.store?.slug);

const normalizedPosts = computed(() => {
  return liveHistoryList.value.map((post: any) => {
    if (!post.publications?.[0]?.started_at) {
      console.error('post.publications[0].started_at should not be empty');
    }
    const historyAtString = post.upcoming_at ?? post.publications?.[0]?.started_at;

    return {
      ...post,
      imageUrl: post.image?.[0]?.url ?? '',
      historyAt: format(parseISO(historyAtString), 'yyyy-MM-dd HH:mm'),
      titleDisplay: post.upcoming_title || post.title,
      historyUrl: post.share_url ?? `${STREAM_BASE_URI}/${slug.value}/live/${post.id}`,
    };
  });
});

const needRender = computed(() => [
  liveHistoryList.value.length,
].some(Boolean));

const postStyle = computed(() => {
  const isPostMoreThanOneRow = normalizedPosts.value.length > POST_NUMBER_PER_ROW;

  return {
    '--post-col': isPostMoreThanOneRow ? POST_NUMBER_PER_ROW : normalizedPosts.value.length,
  };
});

const { titleStyle, subtitleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-live-history-display" v-if="needRender">
  <div
    class="module-live-history-display__title"
    v-if="components?.title?.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>

  <div
    class="module-live-history-display__subtitle"
    v-if="components?.subtitle?.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>

  <div class="module-live-history-display__list" :style="postStyle">
    <a
      class="module-live-history-display__list__item"
      v-for="post in normalizedPosts"
      :key="post.id"
      :href="post.historyUrl"
    >
      <div class="module-live-history-display__list__image-wrapper">
        <div class="module-live-history-display__list__image-box module-live-history-display__list__mask">
          <template v-if="post.imageUrl">
            <div class="module-live-history-display__list__image">
              <el-image class="w-full h-full" :src="post.imageUrl" fit="cover"/>
            </div>
            <img class="module-live-history-display__list__play" :src="ICON_POST_PLAY"/>
          </template>
          <template v-else>
            <div class="module-live-history-display__list__placeholder">
              <el-image
                class="module-live-history-display__list__replay"
                :src="ICON_REPLAY"
              />
              <div class="module-live-history-display__list__description">
                {{ $t("cms_setting.display.live_history_placeholder_desc") }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="module-live-history-display__list__content">
        <div class="module-live-history-display__name">
          {{ post.titleDisplay }}
        </div>

        <div class="module-live-history-display__date">
          {{ post.historyAt }}
        </div>
      </div>

      <div class="module-live-history-display__list__arrow">
        <img
          class="module-live-history-display__list__arrow__icon"
          :src="ICON_POST_ARROW"
        />
      </div>
    </a>
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;
$cover-mobile-width: 76px;
$calendar-mobile-width: 132px;

.module-live-history-display {
  @apply px-5 py-4;

  &__title,
  &__subtitle {
    @apply font-medium break-all whitespace-pre-line;

    word-wrap: break-word;
  }

  &__title {
    @apply text-2xl text-center mb-4;
  }

  &__subtitle {
    @apply text-xl text-left mb-4;
  }

  &__list {
    @apply grid gap-x-4 grid-cols-1;

    &__item {
      @apply flex flex-row items-center;
      @apply w-full relative;

      color: inherit;
      -webkit-tap-highlight-color: transparent;

      // NOTE: padding 是為了幫上下的線撐出空間，再用 abosolute 來排版，不然會被 Flex 排成一行
      padding-top: 9px;
      padding-bottom: 8px;

      &:hover {
        @apply no-underline;

        color: inherit;
      }

      &::before {
        @apply h-px bg-gray-500;
        @apply absolute left-0 right-0 top-0;

        content: '';
      }

      &:last-child {
        padding-bottom: 10px;

        &::after {
          @apply h-px bg-gray-500;
          @apply absolute left-0 right-0 bottom-0;

          content: '';
        }
      }
    }

    &__mask {
      @apply transition-all duration-300;

      .module-live-history-display__list__play {
        @apply transition-all duration-300;
      }

      @include rwd-hover {
        @apply opacity-50;

        .module-live-history-display__list__play {
          @apply opacity-0;
        }
      }
    }

    &__image-wrapper {
      flex: 0 0 $cover-mobile-width;
    }

    &__image-box {
      @apply relative flex justify-center items-center bg-gray-600;
      @apply rounded-lg;

      @include aspect-ratio($height: 16, $width: 9);
    }

    &__placeholder {
      @apply flex flex-col items-center justify-center;
      @apply bg-cover;

      background-image: url('@/assets/bg-default-cover.svg');
    }

    &__image,
    &__placeholder {
      @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform;
      @apply w-full h-full;
    }

    &__play {
      @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform;
      @apply w-12 h-12;
    }

    &__replay {
      @apply w-7 h-7;
    }

    &__description {
      @apply hidden;
      @apply text-xs text-center text-gray-900;
      @apply px-4 mt-2;
    }

    &__content {
      @apply mx-4;

      flex: 1 1 100%;
    }

    &__arrow {
      @apply ml-auto mr-2;

      &__icon {
        @apply h-3.5 w-3;
      }
    }
  }

  &__name {
    @apply text-sm font-normal mb-2 line-clamp-3;
  }

  &__date {
    @apply text-xs text-gray-700 font-medium mb-0;
  }
}

// 平板
.tablet .module-live-history-display {
  @apply px-10;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-4 grid-cols-2;

    &__image-wrapper {
      flex: 1 1 auto;
    }

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__replay {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__name {
    @apply mb-1;
  }
}

// PC - 1024px
.laptop .module-live-history-display {
  @apply py-8;

  padding-left: 10%;
  padding-right: 10%;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-8 justify-center;

    // NOTE: 96px 是由上方 gap-x-8 乘上 3 個 gap 所得到
    grid-template-columns: repeat(var(--post-col), minmax(0, calc((100% - 96px) / 4)));

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &__image-wrapper {
        flex: 1 1 auto;
      }

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__replay {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__name {
    @apply mb-1;
  }
}

// PC - 1440px
.desktop .module-live-history-display {
  @apply py-8;

  width: $desktop-fixed-width;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-8 justify-center;

    // NOTE: 96px 是由上方 gap-x-8 乘上 3 個 gap 所得到
    grid-template-columns: repeat(var(--post-col), minmax(0, calc((100% - 96px) / 4)));

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &__image-wrapper {
        flex: 1 1 auto;
      }

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__replay {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__name {
    @apply mb-1;
  }
}
</style>
